<template>
  <div class="space-y-6">
    <div class="bg-white rounded-xl shadow-sm border border-neutral-200 p-6">
      <div class="flex items-center justify-between mb-6">
        <div>
          <h2 class="text-lg font-bold text-neutral-900">SSO 免登站点</h2>
          <p class="text-xs text-neutral-500 mt-1">
            配置接入免登的第三方站点，每个站点独立密钥，账号映射三选一
          </p>
        </div>
        <button
          @click="openAdd"
          class="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 text-sm font-medium flex items-center gap-2"
        >
          <i class="fa fa-plus"></i>新增站点
        </button>
      </div>

      <div v-loading="loading">
        <el-table :data="list" style="width: 100%">
          <el-table-column prop="name" label="站点名称" min-width="140" />
          <el-table-column prop="target" label="站点标识" min-width="120" />
          <el-table-column
            prop="callbackUrl"
            label="回调地址"
            min-width="220"
            show-overflow-tooltip
          />
          <el-table-column label="映射方式" width="100">
            <template #default="scope">{{ mapLabel(scope.row.mapField) }}</template>
          </el-table-column>
          <el-table-column label="状态" width="80">
            <template #default="scope">
              <span :class="scope.row.status === 1 ? 'text-green-600' : 'text-red-500'">
                {{ scope.row.status === 1 ? "启用" : "禁用" }}
              </span>
            </template>
          </el-table-column>
          <el-table-column label="操作" width="170" fixed="right">
            <template #default="scope">
              <button @click="openEdit(scope.row)" class="text-primary hover:underline text-sm mr-3">
                编辑
              </button>
              <button
                @click="toggleStatus(scope.row)"
                class="text-amber-600 hover:underline text-sm mr-3"
              >
                {{ scope.row.status === 1 ? "禁用" : "启用" }}
              </button>
              <button @click="remove(scope.row)" class="text-red-500 hover:underline text-sm">
                删除
              </button>
            </template>
          </el-table-column>
        </el-table>
        <div v-if="!loading && list.length === 0" class="text-center py-16 text-neutral-400">
          暂无站点配置，点击右上角「新增站点」添加
        </div>
      </div>
    </div>

    <!-- 新增/编辑弹窗 -->
    <el-dialog
      v-model="dialogVisible"
      :title="isEdit ? '编辑站点' : '新增站点'"
      width="520px"
      :close-on-click-modal="false"
      destroy-on-close
    >
      <div class="space-y-4">
        <div>
          <label class="block text-sm font-medium text-neutral-700 mb-1">
            站点名称 <span class="text-red-500">*</span>
          </label>
          <input
            v-model="form.name"
            class="w-full px-4 py-2 rounded-lg border border-neutral-300 text-sm"
            placeholder="如：某某工具站"
          />
        </div>
        <div>
          <label class="block text-sm font-medium text-neutral-700 mb-1">
            站点标识 target <span class="text-red-500">*</span>
          </label>
          <input
            v-model="form.target"
            class="w-full px-4 py-2 rounded-lg border border-neutral-300 text-sm"
            placeholder="唯一标识，如 partner_a"
            :disabled="isEdit"
          />
          <p class="text-xs text-neutral-400 mt-1">友情链接里填此值即可免登跳转，新增后不可改</p>
        </div>
        <div>
          <label class="block text-sm font-medium text-neutral-700 mb-1">
            回调地址 callbackUrl <span class="text-red-500">*</span>
          </label>
          <input
            v-model="form.callbackUrl"
            class="w-full px-4 py-2 rounded-lg border border-neutral-300 text-sm"
            placeholder="如 https://对方站.com/sso"
          />
        </div>
        <div>
          <label class="block text-sm font-medium text-neutral-700 mb-1">账号映射方式</label>
          <select
            v-model="form.mapField"
            class="w-full px-4 py-2 rounded-lg border border-neutral-300 text-sm"
          >
            <option value="uid">用户ID（uid）</option>
            <option value="phone">手机号（phone）</option>
            <option value="loginId">登录名（loginId）</option>
          </select>
        </div>
        <div v-if="!isEdit">
          <label class="block text-sm font-medium text-neutral-700 mb-1">共享密钥</label>
          <div class="flex gap-2">
            <input
              v-model="form.secret"
              class="flex-1 px-4 py-2 rounded-lg border border-neutral-300 text-sm font-mono"
              placeholder="留空则保存时自动生成，也可手工填写"
            />
            <button
              @click="genSecret"
              class="px-3 py-2 rounded-lg border border-neutral-300 text-sm hover:border-primary hover:text-primary whitespace-nowrap"
            >
              随机生成
            </button>
          </div>
        </div>
        <div>
          <label class="block text-sm font-medium text-neutral-700 mb-1">状态</label>
          <select
            v-model="form.status"
            class="w-full px-4 py-2 rounded-lg border border-neutral-300 text-sm"
          >
            <option :value="1">启用</option>
            <option :value="0">禁用</option>
          </select>
        </div>
      </div>
      <template #footer>
        <button
          @click="dialogVisible = false"
          class="px-4 py-2 rounded-lg border border-neutral-300 text-sm mr-3"
        >
          取消
        </button>
        <button @click="save" class="px-4 py-2 bg-primary text-white rounded-lg text-sm">
          确定
        </button>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import { ElMessage, ElMessageBox } from "element-plus";

export default {
  name: "SsoManager",
  data() {
    return {
      list: [],
      loading: false,
      dialogVisible: false,
      isEdit: false,
      form: {},
    };
  },
  created() {
    this.getList();
  },
  methods: {
    async getList() {
      this.loading = true;
      try {
        const res = await this.$axios({
          url: "sso/admin/list",
          method: "POST",
          data: {},
        });
        if (res.code === 200) this.list = res.data.list || [];
      } catch (e) {
        this.list = [];
      } finally {
        this.loading = false;
      }
    },
    mapLabel(f) {
      return { uid: "用户ID", phone: "手机号", loginId: "登录名" }[f] || f;
    },
    openAdd() {
      this.isEdit = false;
      this.form = {
        name: "",
        target: "",
        callbackUrl: "",
        mapField: "uid",
        secret: "",
        status: 1,
      };
      this.dialogVisible = true;
    },
    openEdit(row) {
      this.isEdit = true;
      this.form = {
        id: row.id,
        name: row.name,
        target: row.target,
        callbackUrl: row.callbackUrl,
        mapField: row.mapField || "uid",
        status: row.status,
      };
      this.dialogVisible = true;
    },
    async genSecret() {
      try {
        const res = await this.$axios({ url: "sso/secret", method: "GET" });
        if (res.code === 200) this.form.secret = res.data;
      } catch (e) {
        ElMessage.error("生成失败");
      }
    },
    async save() {
      if (!this.form.name || !this.form.target || !this.form.callbackUrl) {
        ElMessage.warning("请填写站点名称、标识和回调地址");
        return;
      }
      try {
        const data = await this.$aesEncrypt(this.form);
        const res = await this.$axios({ url: "sso", method: "PUT", data: { data } });
        if (res.code === 200) {
          ElMessage.success("保存成功");
          this.dialogVisible = false;
          this.getList();
        } else {
          ElMessage.error(res.msg || "保存失败");
        }
      } catch (e) {
        ElMessage.error("保存失败，请重试");
      }
    },
    async toggleStatus(row) {
      try {
        const payload = {
          id: row.id,
          name: row.name,
          target: row.target,
          callbackUrl: row.callbackUrl,
          mapField: row.mapField,
          status: row.status === 1 ? 0 : 1,
        };
        const data = await this.$aesEncrypt(payload);
        const res = await this.$axios({ url: "sso", method: "PUT", data: { data } });
        if (res.code === 200) {
          ElMessage.success("操作成功");
          this.getList();
        } else {
          ElMessage.error(res.msg || "操作失败");
        }
      } catch (e) {
        ElMessage.error("操作失败");
      }
    },
    remove(row) {
      ElMessageBox.confirm(`确定删除站点「${row.name}」吗？`, "删除站点", {
        type: "error",
        confirmButtonText: "删除",
        cancelButtonText: "取消",
      }).then(async () => {
        const res = await this.$axios({ url: `sso/${row.id}`, method: "DELETE" });
        if (res.code === 200) {
          ElMessage.success("删除成功");
          this.getList();
        } else {
          ElMessage.error(res.msg || "删除失败");
        }
      });
    },
  },
};
</script>
